import { useState } from 'react';
import type { ApiClientErrorCode, Handle, HandleKind } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Lightbox } from '@/components/Lightbox/Lightbox';
import { ForwardSheet } from '@/components/ForwardSheet/ForwardSheet';
import { buildCardShareUrl } from '@/lib/shareUrl';

/**
 * Presenter Экрана 2.4.3 — карточка ручки/крючка. См. ADR-0005.
 *
 * 4 состояния (P-021): loading / success / not-found / error.
 * `not-found` отделён от общей `error` — у них разный текст и разная семантика
 * (404 = «такого товара нет в каталоге», 5xx/network = «сервер не ответил»).
 *
 * Ф-Фронт (ADR-0070, паритет с DecorCardView):
 *   - Hero: реальное фото из R2 (signed-URL, `heroImageUrl`); тап → зум на весь
 *     экран (Lightbox). Нет фото / onError → плейсхолдер (инициал бренда / «?»).
 *   - «Переслать клиенту» → ForwardSheet (WhatsApp/Telegram/MAX/копировать, P-019).
 *   - Пустой `brand` (магазин-заглушка «Не известно N», ADR-0070 §4) — строку
 *     «Бренд» СКРЫВАЕМ; в тексте пересылки бренд опускаем.
 */
export type HandleCardState = 'loading' | 'success' | 'not-found' | 'error';

export interface HandleCardViewProps {
  state: HandleCardState;
  data?: Handle;
  error?: { code: ApiClientErrorCode; message: string };
  /** Ф-Фронт: signed-URL фото для hero. null/undefined или onError → плейсхолдер. */
  heroImageUrl?: string | null;
  onShopsClick: () => void;
  /** «Скопировать сообщение» в меню пересылки — clipboard в Container. */
  onCopyMessage: (text: string) => void;
  onBackToCatalog: () => void;
}

/** Человеческое имя изделия per handle-kind (текст пересылки). */
const FORWARD_NOUN: Record<HandleKind, string> = {
  handle: 'Ручка',
  hook: 'Крючок',
};

/**
 * Текст пересылки БЕЗ URL. «Ручка/Крючок, <name>, <brand>, смотрите по ссылке».
 * Пустой `brand` (магазин-заглушка) — бренд опускаем (ADR-0070 §4): «Ручка,
 * <name>, смотрите по ссылке».
 */
function forwardTextNoUrl(handle: Handle): string {
  const brandPart = handle.brand.trim() !== '' ? `, ${handle.brand}` : '';
  return `${FORWARD_NOUN[handle.kind]}, ${handle.name}${brandPart}, смотрите по ссылке`;
}

export function HandleCardView(props: HandleCardViewProps) {
  const { state, data } = props;
  // Header держит generic «Карточка» — конкретное имя товара рендерится в контенте
  // как h2 (избегаем двух одинаковых heading, иначе getByRole находит multiple).
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Карточка" />

      <section className="flex-1 p-6 overflow-y-auto">
        {state === 'loading' && <LoadingState />}
        {state === 'success' && data && (
          <SuccessState
            data={data}
            heroImageUrl={props.heroImageUrl}
            onShopsClick={props.onShopsClick}
            onCopyMessage={props.onCopyMessage}
          />
        )}
        {state === 'not-found' && <NotFoundState onBackToCatalog={props.onBackToCatalog} />}
        {state === 'error' && <ErrorState onBackToCatalog={props.onBackToCatalog} />}
      </section>

      <BottomBar />
    </main>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
      <p className="text-muted text-[18px] m-0">Загружаем…</p>
    </div>
  );
}

function SuccessState({
  data,
  heroImageUrl,
  onShopsClick,
  onCopyMessage,
}: {
  data: Handle;
  heroImageUrl?: string | null;
  onShopsClick: () => void;
  onCopyMessage: (text: string) => void;
}) {
  const [heroImgFailed, setHeroImgFailed] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Зум фото — локальное состояние экрана (P-036): закрывается тапом/крестиком/Esc
  // внутри Lightbox, НЕ через bottom-bar «Назад» (та ведёт на пред. экран).
  const [zoomOpen, setZoomOpen] = useState(false);
  const showHeroImage = Boolean(heroImageUrl) && !heroImgFailed;
  const hasBrand = data.brand.trim() !== '';
  // Плейсхолдер: инициал бренда; у магазин-заглушек (пустой brand) — «?».
  const placeholderChar = hasBrand ? data.brand.charAt(0) : '?';

  return (
    <div className="flex flex-col gap-6">
      {/* Hero: реальное фото из R2 (Ф-Фронт), fallback — плейсхолдер с инициалом.
          При наличии фото — тап открывает полноэкранный зум (Lightbox). */}
      {showHeroImage ? (
        <button
          type="button"
          onClick={() => setZoomOpen(true)}
          aria-label={`Открыть фото на весь экран: ${data.name}`}
          className="block w-full aspect-square rounded-xl border-[1.5px] border-icon-border overflow-hidden bg-card p-0 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
        >
          <img
            src={heroImageUrl as string}
            alt=""
            // object-contain: товар целиком на белом bg-card (не обрезан кропом) —
            // хорошо на любой ширине, особенно в десктоп-рамке (desktop-mobile-frame).
            className="w-full h-full object-contain"
            onError={() => setHeroImgFailed(true)}
          />
        </button>
      ) : (
        <div
          className="w-full aspect-square rounded-xl bg-icon-bg border-[1.5px] border-icon-border flex items-center justify-center"
          aria-label="Фото товара (заглушка)"
        >
          <span className="font-display text-ink" style={{ fontSize: 120 }}>
            {placeholderChar}
          </span>
        </div>
      )}
      {showHeroImage && zoomOpen && (
        <Lightbox src={heroImageUrl as string} alt={data.name} onClose={() => setZoomOpen(false)} />
      )}

      <div>
        <h2 className="font-display text-h2 text-ink m-0">{data.name}</h2>
        {/* Пустой brand (магазин-заглушка) — строку «Бренд» скрываем (ADR-0070 §4). */}
        {hasBrand && (
          <p className="text-muted text-base mt-2">
            Бренд: <span className="text-ink font-semibold">{data.brand}</span>
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Chip>{data.color}</Chip>
        {data.shape && <Chip>{data.shape}</Chip>}
        {data.mountType && <Chip>{data.mountType}</Chip>}
        {data.sizeMm && <Chip>Размер: {data.sizeMm} мм</Chip>}
      </div>

      <hr className="border-icon-border" />

      {/* Вертикальный список действий (P-019 MUST). */}
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onShopsClick}
          aria-label={`Найти в магазинах: ${data.name}`}
          className={[
            'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
            'font-bold text-[18px] py-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          Найти в магазинах
        </button>

        <button
          type="button"
          onClick={() => setShareOpen(true)}
          aria-label="Переслать клиенту"
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          <span aria-hidden="true">↗</span>
          <span className="flex-1 text-left">Переслать клиенту</span>
        </button>
      </div>

      <ForwardSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        message={forwardTextNoUrl(data)}
        url={buildCardShareUrl('handles', {
          kind: data.kind,
          globalId: data.globalId,
          shortId: data.shortId,
        })}
        onCopyMessage={onCopyMessage}
      />
    </div>
  );
}

function NotFoundState({ onBackToCatalog }: { onBackToCatalog: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">Товар не найден</p>
      <button
        type="button"
        onClick={onBackToCatalog}
        aria-label="Назад в каталог"
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Назад в каталог
      </button>
    </div>
  );
}

function ErrorState({ onBackToCatalog }: { onBackToCatalog: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      <button
        type="button"
        onClick={onBackToCatalog}
        aria-label="Назад в каталог"
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Назад в каталог
      </button>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-base px-3 py-1 rounded-full bg-icon-bg text-separator">{children}</span>
  );
}
